import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

interface PlanSelectionScreenProps {
  onSelectPlan: (plan: 'monthly' | 'threeMonths') => void;
  onClose: () => void;
  onBack: () => void;
  isLoading?: boolean;
  isLegacyUser?: boolean;
  isPostTrialUser?: boolean;
}

const PlanSelectionScreen: React.FC<PlanSelectionScreenProps> = ({
  onSelectPlan,
  onClose,
  onBack,
  isLoading = false,
  isLegacyUser = false,
  isPostTrialUser = false
}) => {
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'threeMonths'>('threeMonths');

  const handleContinue = () => {
    onSelectPlan(selectedPlan);
  };

  const prices = isLegacyUser ? {
    monthly: { total: 149, perMonth: 149 },
    threeMonths: { total: 297, perMonth: 99 }
  } : {
    monthly: { total: 399, perMonth: 399 },
    threeMonths: { total: 897, perMonth: 299 }
  };

  const getSubtitleText = () => {
    if (isPostTrialUser) {
      if (selectedPlan === 'monthly') {
        return (
          <>
            Platba {prices.monthly.total} Kč měsíčně.
            <br />
            Předplatné můžeš kdykoliv zrušit.
          </>
        );
      }
      return (
        <>
          Platba celkem {prices.threeMonths.total} Kč
          <br />
          (1× za 3 měsíce). Předplatné můžeš kdykoliv zrušit.
        </>
      );
    }

    if (selectedPlan === 'monthly') {
      return (
        <>
          3 dny zkušební doby zdarma, poté {prices.monthly.total} Kč měsíčně.
          <br />
          Předplatné můžeš kdykoliv zrušit.
        </>
      );
    }
    return (
      <>
        3 dny zkušební doby zdarma, poté celkem {prices.threeMonths.total} Kč
        <br />
        (1× za 3 měsíce). Předplatné můžeš kdykoliv zrušit.
      </>
    );
  };

  return (
    <div className="w-full max-w-2xl px-6 flex flex-col items-center">

      {/* Heading */}
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
        className="text-3xl sm:text-4xl font-extrabold text-white text-center mb-8"
      >
        {isPostTrialUser ? (
          <>Vyber si svůj plán</>
        ) : (
          <>Vyber si svůj plán po <br />zkušební době</>
        )}
      </motion.h1>

      {/* Plan Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="w-full max-w-sm mx-auto space-y-4 mb-12"
      >
        {/* Monthly Plan */}
        <div
          onClick={() => setSelectedPlan('monthly')}
          className={`relative transition-all duration-300 transform cursor-pointer ${selectedPlan === 'monthly'
            ? 'scale-105 z-20'
            : 'scale-95 z-10'
            }`}
        >
          {/* Gradient border when selected */}
          {selectedPlan === 'monthly' && (
            <div className="absolute -inset-1 bg-gradient-to-r from-teal-400 via-blue-500 to-purple-500 rounded-xl blur-[1px]" />
          )}

          <div
            className={`relative rounded-xl px-5 py-8 flex items-center h-24 ${selectedPlan === 'monthly'
              ? 'bg-white shadow-xl'
              : 'bg-white/70 opacity-80'
              }`}
          >
            <div className="flex items-center justify-between w-full">
              <h3 className={`font-extrabold text-2xl sm:text-3xl tracking-tight whitespace-nowrap ${selectedPlan === 'monthly'
                ? 'text-blue-600'
                : 'text-blue-600/70'
                }`}>
                1 měsíc
              </h3>
              <div className="flex items-baseline gap-1 whitespace-nowrap">
                <span className={`font-sans font-extrabold text-base sm:text-xl ${selectedPlan === 'monthly'
                  ? 'text-blue-600'
                  : 'text-blue-600/70'
                  }`}>
                  {prices.monthly.perMonth} Kč
                </span>
                <span className={`font-sans font-extrabold text-base sm:text-xl ${selectedPlan === 'monthly' ? 'text-blue-600' : 'text-blue-600/70'
                  }`}>
                  / měsíčně
                </span>
              </div>
            </div>

            {/* Checkmark for Monthly when selected - top right outside */}
            {selectedPlan === 'monthly' && (
              <div className="absolute -top-3 -right-3 w-7 h-7 rounded-full flex items-center justify-center shadow-lg z-30" style={{ backgroundColor: '#a554f0' }}>
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </div>
        </div>

        {/* 3 Months Plan */}
        <div
          onClick={() => setSelectedPlan('threeMonths')}
          className={`relative transition-all duration-300 transform cursor-pointer ${selectedPlan === 'threeMonths'
            ? 'scale-105 z-20'
            : 'scale-95 z-10'
            }`}
        >
          {/* Gradient border when selected */}
          {selectedPlan === 'threeMonths' && (
            <div className="absolute -inset-1 bg-gradient-to-r from-teal-400 via-blue-500 to-purple-500 rounded-xl blur-[1px]" />
          )}

          <div
            className={`relative rounded-xl px-5 py-8 flex items-center h-24 ${selectedPlan === 'threeMonths'
              ? 'bg-white shadow-xl'
              : 'bg-white/70 opacity-80'
              }`}
          >
            {/* Badge - positioned at top-left corner inside */}
            <div className="absolute left-0 z-10" style={{ top: '-1px' }}>
              <span className="font-sans text-white text-sm font-extrabold px-4 py-1 rounded-none rounded-br-md rounded-tl-xl uppercase tracking-wide shadow-sm" style={{ background: 'linear-gradient(to right, #2dd4bf, #3998e5)' }}>
                Nejvýhodnější
              </span>
            </div>

            <div className="flex items-center justify-between w-full">
              <h3 className={`font-extrabold text-2xl sm:text-3xl tracking-tight whitespace-nowrap ${selectedPlan === 'threeMonths'
                ? 'text-blue-600'
                : 'text-blue-600/50'
                }`}>
                3 měsíce
              </h3>
              <div className="flex items-baseline gap-1 whitespace-nowrap">
                <span className={`font-sans font-extrabold text-base sm:text-xl ${selectedPlan === 'threeMonths'
                  ? 'text-blue-600'
                  : 'text-blue-600/50'
                  }`}>
                  {prices.threeMonths.perMonth} Kč
                </span>
                <span className={`font-sans font-extrabold text-base sm:text-xl ${selectedPlan === 'threeMonths' ? 'text-blue-600' : 'text-blue-600/50'
                  }`}>
                  / měsíčně
                </span>
              </div>
            </div>

            {/* Checkmark for 3 months when selected - top right outside */}
            {selectedPlan === 'threeMonths' && (
              <div className="absolute -top-3 -right-3 w-7 h-7 rounded-full flex items-center justify-center shadow-lg z-30" style={{ backgroundColor: '#a554f0' }}>
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Subtitle */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.4 }}
        className="text-white text-center text-sm sm:text-base font-medium mb-8 px-4"
      >
        {getSubtitleText()}
      </motion.p>

      {/* CTA Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        className="w-full max-w-sm mx-auto"
      >
        <button
          onClick={handleContinue}
          disabled={isLoading}
          className="w-full bg-white hover:bg-gray-50 disabled:opacity-80 text-gray-900 text-lg font-bold py-4 sm:py-5 rounded-2xl transition-all border-b-[6px] border-gray-300 active:border-b-2 active:translate-y-[4px] disabled:active:border-b-[6px] disabled:active:translate-y-0 shadow-lg shadow-white/10 flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              {isPostTrialUser ? 'Aktivuji Premium...' : 'Připravujeme...'}
            </>
          ) : (
            isPostTrialUser ? 'Aktivovat Premium' : 'Zahájit zkušební dobu na 3 dny'
          )}
        </button>
      </motion.div>
    </div>
  );
};

export default PlanSelectionScreen;